import { Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pencil, Plus, Trash2, Layers, Building2, X } from "lucide-react";
import { DataCard, DetailRow } from "@/components/ui/data-card";
import { ResponsiveTableShell } from "@/components/ui/responsive-table-shell";
import { ExpandingSearch } from "@/components/ui/expanding-search";
import { ChipFilter } from "@/components/ui/chip-filter";
import { AddFilterMenu } from "@/components/ui/add-filter-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TablePagination } from "@/components/ui/table-pagination";
import { useTablePagination } from "@/hooks/useTablePagination";
import { toast } from "sonner";
import { Location, DAYS_OF_WEEK, Customer, Tractor, Driver } from "@/types/tms";
import { useAuth } from "@/hooks/useAuth";
import { useTrailerTypes, getTrailerTypeLabel } from "@/hooks/useTrailerTypes";
import { useOrderCancellation } from "@/hooks/useOrderDeletion";
import { OrderFormDialog } from "@/components/orders/OrderFormDialog";
import { OrderFormData, initialFormData } from "@/components/orders/orderFormTypes";
import { useOrderMutations } from "@/hooks/useOrderMutations";
import {
  templateToOrderFormData,
  orderFormToTemplatePatch,
  orderFormToTemplateLegRows,
  isOpenEndedTemplate,
  type TemplateLegRow,
  type TemplateRow,
  templateScheduleInPlanningTz,
} from "@/lib/recurringTemplateForm";
import { PLANNING_TZ } from "@/lib/timezone";
import { formatInTimeZone } from "date-fns-tz";

interface RecurringOrdersSubTableProps {
  /** Omit to list templates across all customers (Orders page tab). */
  customerId?: string;
  customerShortCode?: string;
  hideHeader?: boolean;
  createOpen?: boolean;
  onCreateOpenChange?: (open: boolean) => void;
}

interface RecurringTemplate {
  id: string;
  unique_id: string;
  ship_from_location_id: string | null;
  ship_to_location_id: string | null;
  departure_time: string;
  trip_duration_minutes: number;
  recurrence_days: number[];
  start_date: string;
  end_date: string;
  trailer_type_id: string | null;
  order_type?: string;
  /** Populated from the embedded `recurring_order_template_legs(...)` join.
   *  Non-empty when the template is multi-leg. */
  recurring_order_template_legs?: TemplateLegRow[];
}

export function RecurringOrdersSubTableUnified({
  customerId,
  customerShortCode,
  hideHeader = false,
  createOpen,
  onCreateOpenChange,
}: RecurringOrdersSubTableProps) {
  const { user } = useAuth();
  const { activeTenant, tenantId } = useTenant();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [internalCreateOpen, setInternalCreateOpen] = useState(false);
  const isControlledCreate = createOpen !== undefined;
  const isCreateOpen = isControlledCreate ? createOpen : internalCreateOpen;
  const setIsCreateOpen = (open: boolean) => {
    if (!isControlledCreate) setInternalCreateOpen(open);
    onCreateOpenChange?.(open);
  };
  const [editingId, setEditingId] = useState<string | null>(null);
  const [orderFormData, setOrderFormData] = useState<OrderFormData>(initialFormData);
  // Snapshot of the template at the moment edit was opened. Used to compute
  // the propagation patch and pass `originalTemplate` to `updateMutation`.
  const [editingOriginal, setEditingOriginal] = useState<RecurringTemplate | null>(null);
  const { data: trailerTypes } = useTrailerTypes();
  const { bulkCancelAsync } = useOrderCancellation();

  // Lookups required by OrderFormDialog. Customers is scoped to the row's
  // billing party (locked in the form) but the dialog still needs the array.
  const { data: customers } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data } = await supabase.from("customers").select("*").order("organization_name");
      return data as Customer[];
    },
  });
  const { data: tractors } = useQuery({
    queryKey: ["tractors"],
    queryFn: async () => {
      const { data } = await supabase.from("tractors").select("*").order("name");
      return data as Tractor[];
    },
  });
  const { data: locations } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("locations")
        .select("*")
        .eq("archived", false)
        .order("short_code");
      if (error) throw error;
      return data as Location[];
    },
  });

  const { data: drivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: async () => {
      const { data } = await supabase.from("drivers").select("*").order("name");
      return data as Driver[];
    },
  });

  // Reuses the canonical recurring create path: it inserts a template, fans
  // out child orders, links them via recurring_order_template_id, and fires
  // `onTemplateCreated`. This keeps both creation flows (Orders page and
  // Customers > Recurring Order Templates) writing through the same logic.
  const { createMutation: orderFormCreateMutation } = useOrderMutations({
    userEmail: user?.email,
    tractors,
    drivers,
    locations,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-templates", customerId] });
    },
    onTemplateCreated: (templateId) => {
      setIsCreateOpen(false);
      setOrderFormData(initialFormData);
      navigate(`/templates/${templateId}`);
    },
  });

  const handleOrderFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    orderFormCreateMutation.mutate(orderFormData);
  };

  const handleOrderFormReset = () => {
    setOrderFormData(initialFormData);
    setIsCreateOpen(false);
  };

  const { data: templates, isLoading } = useQuery({
    queryKey: ["recurring-templates", customerId],
    queryFn: async () => {
      let query = supabase
        .from("recurring_order_templates")
        .select("*, recurring_order_template_legs(*)");
      if (customerId) query = query.eq("billing_party_id", customerId);
      const { data, error } = await query.order("unique_id");
      if (error) throw error;
      return data as unknown as RecurringTemplate[];
    },
  });

  // Note: the legacy `createMutation` that inserted a bare template row was
  // removed — all creation now flows through `orderFormCreateMutation` above
  // (OrderFormDialog + useOrderMutations), which handles single- and
  // multi-leg templates uniformly and writes `recurring_order_template_legs`.

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
      originalTemplate,
      legRows,
    }: {
      id: string;
      data: Partial<RecurringTemplate>;
      originalTemplate: RecurringTemplate;
      /** When present, replaces the template's `recurring_order_template_legs`
       *  in a delete-then-insert (safe under the unique (template_id,
       *  leg_sequence) constraint). Only supplied by the multi-leg edit
       *  path — omit for single-leg templates. */
      legRows?: Record<string, unknown>[];
    }) => {
      await supabase.rpc("set_tenant_slug", { _slug: activeTenant });
      // Update the template first
      const { error } = await supabase
        .from("recurring_order_templates")
        .update(data)
        .eq("id", id);
      if (error) throw error;

      // Multi-leg: rewrite the template_legs blueprint. Delete-then-insert
      // avoids matching stale sequences and keeps the generator's next run
      // in sync with the form's current leg list.
      if (legRows) {
        const { error: delErr } = await supabase
          .from("recurring_order_template_legs")
          .delete()
          .eq("template_id", id);
        if (delErr) throw delErr;
        if (legRows.length > 0) {
          const { error: insErr } = await supabase
            .from("recurring_order_template_legs")
            .insert(legRows as any);
          if (insErr) throw insErr;
        }
      }

      // Handle removed days: cancel booked orders on those days
      // Compare weekdays in Central: a save always stores Central, and a
      // legacy Pacific template's days may have shifted forward on conversion.
      const oldDays = templateScheduleInPlanningTz({
        departure_time: originalTemplate.departure_time,
        start_date: originalTemplate.start_date,
        end_date: originalTemplate.end_date,
        recurrence_days: originalTemplate.recurrence_days || [],
        timezone: (originalTemplate as any).timezone,
      }).recurrence_days;
      const newDays = data.recurrence_days ?? oldDays;
      const removedDays = oldDays.filter(d => !newDays.includes(d));

      // Cancel booked orders for removed days (SOX-compliant: no hard delete)
      if (removedDays.length > 0) {
        const { data: bookedOrders, error: fetchBookedError } = await supabase
          .from("orders")
          .select("id, ready_time")
          .eq("recurring_order_template_id", id)
          .eq("status", "booked");

        if (fetchBookedError) throw fetchBookedError;

        if (bookedOrders && bookedOrders.length > 0) {
          const ordersToCancel = bookedOrders.filter(order => {
            // Day-of-week in Central (the template's timezone after save) so
            // operators in any browser timezone see the same cancellation set
            // as the generator.
            const ctDow = parseInt(formatInTimeZone(new Date(order.ready_time), PLANNING_TZ, "i"), 10) % 7;
            const dayOfWeek = Number.isFinite(ctDow) ? ctDow : new Date(order.ready_time).getDay();
            return removedDays.includes(dayOfWeek);
          });

          if (ordersToCancel.length > 0) {
            // Sync-then-Persist via the canonical hook: each affected order
            // gets its upstream EAPI trip cancelled before the local update,
            // and BoL cleanup runs as a side effect.
            await bulkCancelAsync(
              ordersToCancel.map(o => o.id),
              {
                reason: "Template updated",
                notes: "Recurrence day removed from template",
              },
            );
          }
        }
      }

      // Propagate other changes to remaining associated orders in 'booked' status
      const { data: associatedOrders, error: fetchError } = await supabase
        .from("orders")
        .select("id, trailer_type_id, ship_from_city, ship_to_city, ready_time, requested_delivery_time")
        .eq("recurring_order_template_id", id)
        .eq("status", "booked");
      
      if (fetchError) throw fetchError;
      if (!associatedOrders || associatedOrders.length === 0) return;

      // Track changes for logging (non-time fields)
      const bulkChanges: { field_name: string; old_value: string | null; new_value: string | null }[] = [];

      // Check trailer_type_id changes
      if (data.trailer_type_id !== undefined && data.trailer_type_id !== originalTemplate.trailer_type_id) {
        bulkChanges.push({
          field_name: "trailer_type_id",
          old_value: getTrailerTypeLabel(originalTemplate.trailer_type_id, trailerTypes),
          new_value: getTrailerTypeLabel(data.trailer_type_id, trailerTypes),
        });
      }

      // Check ship_from_location changes
      let shipFromUpdates: Record<string, any> = {};
      if (data.ship_from_location_id !== undefined && data.ship_from_location_id !== originalTemplate.ship_from_location_id) {
        const newLocation = locations?.find(l => l.id === data.ship_from_location_id);
        const oldLocation = locations?.find(l => l.id === originalTemplate.ship_from_location_id);
        if (newLocation) {
          shipFromUpdates = {
            ship_from_street_address: newLocation.street_address || "",
            ship_from_city: newLocation.city || "",
            ship_from_province_state: newLocation.province_state || null,
            ship_from_postal_zip_code: newLocation.postal_zip_code || null,
            ship_from_country: newLocation.country || null,
          };
          bulkChanges.push({
            field_name: "ship_from_city",
            old_value: oldLocation?.city || null,
            new_value: newLocation.city || null,
          });
        }
      }

      // Check ship_to_location changes
      let shipToUpdates: Record<string, any> = {};
      if (data.ship_to_location_id !== undefined && data.ship_to_location_id !== originalTemplate.ship_to_location_id) {
        const newLocation = locations?.find(l => l.id === data.ship_to_location_id);
        const oldLocation = locations?.find(l => l.id === originalTemplate.ship_to_location_id);
        if (newLocation) {
          shipToUpdates = {
            ship_to_street_address: newLocation.street_address || "",
            ship_to_city: newLocation.city || "",
            ship_to_province_state: newLocation.province_state || null,
            ship_to_postal_zip_code: newLocation.postal_zip_code || null,
            ship_to_country: newLocation.country || null,
          };
          bulkChanges.push({
            field_name: "ship_to_city",
            old_value: oldLocation?.city || null,
            new_value: newLocation.city || null,
          });
        }
      }

      // Check if departure_time or trip_duration_minutes changed
      const departureChanged = data.departure_time !== undefined && data.departure_time !== originalTemplate.departure_time;
      const durationChanged = data.trip_duration_minutes !== undefined && data.trip_duration_minutes !== originalTemplate.trip_duration_minutes;
      const timeFieldsChanged = departureChanged || durationChanged;

      const newDepartureTime = data.departure_time ?? originalTemplate.departure_time;
      const newDurationMinutes = data.trip_duration_minutes ?? originalTemplate.trip_duration_minutes;

      // Build bulk updates for non-time fields
      const bulkOrderUpdates: Record<string, any> = {
        ...(data.trailer_type_id !== undefined && data.trailer_type_id !== originalTemplate.trailer_type_id ? { trailer_type_id: data.trailer_type_id } : {}),
        ...shipFromUpdates,
        ...shipToUpdates,
      };

      // If time fields changed, we need to update each order individually (to preserve dates)
      if (timeFieldsChanged) {
        const allChangeLogEntries: any[] = [];
        const orderUpdateData: { id: string; updates: Record<string, any>; changes: { field_name: string; old_value: string | null; new_value: string | null }[] }[] = [];

        for (const order of associatedOrders) {
          const orderUpdates: Record<string, any> = { ...bulkOrderUpdates };
          const orderChanges: { field_name: string; old_value: string | null; new_value: string | null }[] = [...bulkChanges];

          const existingReadyTime = new Date(order.ready_time);
          const existingDeliveryTime = new Date(order.requested_delivery_time);

          const [hours, minutes] = newDepartureTime.split(":").map(Number);
          const newReadyTime = new Date(existingReadyTime);
          newReadyTime.setHours(hours, minutes, 0, 0);

          const newDeliveryTime = new Date(newReadyTime.getTime() + newDurationMinutes * 60 * 1000);

          orderUpdates.ready_time = newReadyTime.toISOString();
          orderUpdates.requested_delivery_time = newDeliveryTime.toISOString();

          if (departureChanged) {
            orderChanges.push({
              field_name: "ready_time",
              old_value: existingReadyTime.toISOString(),
              new_value: newReadyTime.toISOString(),
            });
          }
          if (durationChanged || departureChanged) {
            orderChanges.push({
              field_name: "requested_delivery_time",
              old_value: existingDeliveryTime.toISOString(),
              new_value: newDeliveryTime.toISOString(),
            });
          }

          orderUpdateData.push({ id: order.id, updates: orderUpdates, changes: orderChanges });

          allChangeLogEntries.push(
            ...orderChanges.map(change => ({
              order_id: order.id,
              changed_by: user?.email || "System",
              field_name: change.field_name,
              old_value: change.old_value,
              new_value: change.new_value,
            }))
          );
        }

        const updateResults = await Promise.all(
          orderUpdateData.map(({ id, updates }) =>
            supabase.from("orders").update(updates).eq("id", id)
          )
        );
        for (const result of updateResults) {
          if (result.error) throw result.error;
        }

        if (allChangeLogEntries.length > 0) {
          const { error: logError } = await supabase.from("order_change_log").insert(allChangeLogEntries);
          if (logError) throw logError;
        }
      } else if (Object.keys(bulkOrderUpdates).length > 0) {
        const { error: updateError } = await supabase
          .from("orders")
          .update(bulkOrderUpdates)
          .eq("recurring_order_template_id", id)
          .eq("status", "booked");
        
        if (updateError) throw updateError;

        if (bulkChanges.length > 0) {
          const changeLogEntries = associatedOrders.flatMap(order => 
            bulkChanges.map(change => ({
              order_id: order.id,
              changed_by: user?.email || "System",
              field_name: change.field_name,
              old_value: change.old_value,
              new_value: change.new_value,
            }))
          );
          await supabase.from("order_change_log").insert(changeLogEntries);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-templates", customerId] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Template updated and changes propagated to booked orders");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Cancel all booked orders linked to this template via the canonical
      // hook (SOX-compliant + Sync-then-Persist for any active EAPI trips).
      const { data: bookedRows } = await supabase
        .from("orders")
        .select("id")
        .eq("recurring_order_template_id", id)
        .eq("status", "booked");
      if (bookedRows && bookedRows.length > 0) {
        await bulkCancelAsync(
          bookedRows.map(r => r.id),
          {
            reason: "Template deleted",
            notes: "Recurring order template was deleted",
          },
        );
      }

      const { error } = await supabase
        .from("recurring_order_templates")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring-templates", customerId] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Template deleted and associated booked orders canceled");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const generateUniqueId = () => {
    const existingNumbers = (templates || [])
      .map((t) => {
        const match = t.unique_id.match(/-REC-(\d+)$/);
        return match ? parseInt(match[1], 10) : 0;
      });
    const maxNum = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
    return `${customerShortCode}-REC-${String(maxNum + 1).padStart(3, "0")}`;
  };

  // ── Edit path: reuse OrderFormDialog (templateEditMode) so the edit form
  // shows the same fields as Create. Shared helpers (see
  // `src/lib/recurringTemplateForm.ts`) handle the template ↔ OrderFormData
  // bridging for both single-leg and multi-leg templates so this file and
  // `RecurringTemplateDetail` stay in lock-step.

  const handleEditClick = (template: RecurringTemplate) => {
    setEditingOriginal(template);
    setOrderFormData(
      templateToOrderFormData(
        template as unknown as TemplateRow,
        (template.recurring_order_template_legs || []) as TemplateLegRow[],
        locations,
      ),
    );
    setEditingId(template.id);
  };

  const handleEditFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId || !editingOriginal || !tenantId) return;
    const isMultiLeg = orderFormData.order_type === "multi-leg";
    const patch = orderFormToTemplatePatch(orderFormData);
    const legRows = isMultiLeg
      ? orderFormToTemplateLegRows(orderFormData, editingId, tenantId, locations)
      : undefined;
    try {
      await updateMutation.mutateAsync({
        id: editingId,
        data: patch as Partial<RecurringTemplate>,
        originalTemplate: editingOriginal,
        legRows,
      });
      // Best-effort: re-run inline generation so any newly eligible
      // occurrences within 48h reflect the edited leg blueprint.
      supabase.functions
        .invoke("trigger-template-generation", { body: { template_id: editingId } })
        .catch((err) => console.warn("Inline template regeneration failed:", err));
      setEditingId(null);
      setEditingOriginal(null);
      setOrderFormData(initialFormData);
    } catch {
      // toast handled by mutation onError; keep dialog open for retry
    }
  };

  const handleEditFormReset = () => {
    setEditingId(null);
    setEditingOriginal(null);
    setOrderFormData(initialFormData);
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [customerFilter, setCustomerFilter] = useState<string[]>([]);
  const [customerFilterActive, setCustomerFilterActive] = useState(false);
  const [customerFilterOpen, setCustomerFilterOpen] = useState(false);
  const [groupBy, setGroupBy] = useState<"customer" | "none">("customer");

  const getLocationName = (id: string | null) => {
    if (!id) return "—";
    return locations?.find((l) => l.id === id)?.short_code || "Unknown";
  };

  const rawRows = templates || [];
  const search = searchQuery.trim().toLowerCase();
  const filteredRows = rawRows.filter((row) => {
    const selectedCustomers = customerFilter.filter((v) => v !== "all");
    if (selectedCustomers.length && !selectedCustomers.includes((row as any).billing_party_id ?? "none")) {
      return false;
    }
    if (!search) return true;
    const haystack = [
      row.unique_id,
      getLocationName(row.ship_from_location_id),
      getLocationName(row.ship_to_location_id),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(search);
  });

  const pagination = useTablePagination(filteredRows);
  const allRows = pagination.pageRows;

  const customerFilterOptions = (customers || [])
    .map((c: any) => ({
      value: c.id,
      label: c.organization_name || c.short_code || c.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Orders-page (all-customers) view groups templates under a customer heading.
  const groupedRows = (() => {
    if (customerId || groupBy !== "customer") return null;
    const nameFor = (id: string | null) => {
      const c = (customers || []).find((cu: any) => cu.id === id);
      return c?.organization_name || c?.short_code || "Unassigned";
    };
    const map = new Map<string, { key: string; name: string; rows: typeof allRows }>();
    for (const row of allRows) {
      const key = (row as any).billing_party_id ?? "none";
      if (!map.has(key)) map.set(key, { key, name: nameFor((row as any).billing_party_id), rows: [] });
      map.get(key)!.rows.push(row);
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  })();

  const formatDays = (days: number[]) => {
    return days.map((d) => DAYS_OF_WEEK.find((dow) => dow.value === d)?.label.slice(0, 3)).join(", ");
  };

  if (isLoading) {
    return <div className="p-4 text-sm text-muted-foreground">Loading...</div>;
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {!customerId && (
            groupBy === "customer" ? (
              <Button
                variant="outline"
                className="gap-1.5 h-9 text-sm border-primary bg-primary/10 text-primary dark:text-[hsl(var(--aurora-tx-link))] hover:bg-primary/15 hover:text-primary dark:hover:text-[hsl(var(--aurora-tx-link))]"
                onClick={() => setGroupBy("none")}
              >
                <Layers className="h-3.5 w-3.5" />
                Grouped by Customer
                <X className="h-3.5 w-3.5 ml-0.5 opacity-70 hover:opacity-100" />
              </Button>
            ) : (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="gap-1.5 h-9 text-sm">
                    <Layers className="h-3.5 w-3.5" />
                    Group by
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-1" align="start" sideOffset={6}>
                  <p className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Group by</p>
                  <button
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent transition-colors text-left"
                    onClick={() => setGroupBy("customer")}
                  >
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                    Customer
                  </button>
                </PopoverContent>
              </Popover>
            )
          )}
          {!customerId && customerFilterActive && (
            <ChipFilter
              label="Customer"
              options={customerFilterOptions}
              value={customerFilter}
              open={customerFilterOpen}
              onOpenChange={setCustomerFilterOpen}
              onChange={(v) => setCustomerFilter(Array.isArray(v) ? v : [v])}
              onRemove={() => {
                setCustomerFilter([]);
                setCustomerFilterActive(false);
                setCustomerFilterOpen(false);
              }}
              multiSelect
            />
          )}
          {!customerId && !customerFilterActive && (
            <AddFilterMenu
              categories={[{ key: "customer", label: "Customer" }]}
              onSelect={() => {
                setCustomerFilterActive(true);
                setCustomerFilterOpen(true);
              }}
            />
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            Showing {filteredRows.length} recurring template{filteredRows.length === 1 ? "" : "s"}
          </span>
          <ExpandingSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search templates..."
          />
          {!hideHeader && (
            <Button size="sm" variant="outline" onClick={() => setIsCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add order template
            </Button>
          )}
        </div>
      </div>

      <OrderFormDialog
        isOpen={isCreateOpen}
        onOpenChange={(open) => {
          setIsCreateOpen(open);
          if (!open) setOrderFormData(initialFormData);
        }}
        editingOrder={null}
        formData={orderFormData}
        setFormData={setOrderFormData}
        onSubmit={handleOrderFormSubmit}
        onReset={handleOrderFormReset}
        isSubmitting={orderFormCreateMutation.isPending}
        customers={customers}
        tractors={tractors}
        drivers={drivers}
        locations={locations}
        forceRecurring
        lockBillingPartyId={customerId}
        titleOverride="New Recurring Order Template"
      />

      <OrderFormDialog
        isOpen={!!editingId}
        onOpenChange={(open) => { if (!open) handleEditFormReset(); }}
        editingOrder={null}
        formData={orderFormData}
        setFormData={setOrderFormData}
        onSubmit={handleEditFormSubmit}
        onReset={handleEditFormReset}
        isSubmitting={updateMutation.isPending}
        customers={customers}
        tractors={tractors}
        drivers={drivers}
        locations={locations}
        templateEditMode
        lockBillingPartyId={customerId}
      />

      {allRows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {search || customerFilter.filter((v) => v !== "all").length
            ? "No templates match your filters."
            : customerId
              ? "No recurring orders for this customer."
              : "No recurring order templates yet."}
        </p>
      ) : (
        <>
        <ResponsiveTableShell
          cards={
            <div className="space-y-3">
              {(groupedRows ?? [{ key: "all", name: "", rows: allRows }]).map((group) => (
                <div key={group.key} className="space-y-3">
                  {group.name && (
                    <h3 className="text-sm font-semibold text-muted-foreground pt-2">{group.name}</h3>
                  )}
                  {group.rows.map((row) => (
                <DataCard key={row.id}>
                  <DataCard.Header>
                    <span className="font-mono text-sm font-semibold truncate">
                      {row.unique_id}
                    </span>
                    <span className="ml-auto flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => handleEditClick(row)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => handleDelete(row.id)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </span>
                  </DataCard.Header>
                  <div className="divide-y divide-border">
                    <DetailRow label="From" value={getLocationName(row.ship_from_location_id)} />
                    <DetailRow label="To" value={getLocationName(row.ship_to_location_id)} />
                    <DetailRow label="Departure" value={row.departure_time} />
                    <DetailRow label="Duration" value={`${row.trip_duration_minutes}m`} />
                    <DetailRow
                      label="Trailer type"
                      value={getTrailerTypeLabel(row.trailer_type_id, trailerTypes) ?? null}
                    />
                    <DetailRow label="Days" value={formatDays(row.recurrence_days) || null} />
                    <DetailRow label="Start" value={row.start_date} />
                    <DetailRow
                      label="End"
                      value={isOpenEndedTemplate(row.end_date) ? "No end date" : row.end_date}
                    />
                  </div>
                </DataCard>
                  ))}
                </div>
              ))}
            </div>
          }
          table={
          <div className="rounded-lg border bg-background overflow-hidden">
          <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[140px]">ID</TableHead>
              <TableHead>From</TableHead>
              <TableHead>To</TableHead>
              <TableHead className="w-[100px]">Departure</TableHead>
              <TableHead className="w-[80px]">Duration</TableHead>
              <TableHead className="w-[120px]">Trailer Type</TableHead>
              <TableHead>Days</TableHead>
              <TableHead className="w-[110px]">Start</TableHead>
              <TableHead className="w-[110px]">End</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(groupedRows ?? [{ key: "all", name: "", rows: allRows }]).map((group) => (
              <Fragment key={group.key}>
                {group.name && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={10} className="bg-muted/50 text-sm font-semibold">
                      {group.name}
                    </TableCell>
                  </TableRow>
                )}
                {group.rows.map((template) => {
              const row = template;
              return (
                <TableRow
                  key={template.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/templates/${template.id}`)}
                >
                  <TableCell>
                    <span className="text-sm">{row.unique_id}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{getLocationName(row.ship_from_location_id)}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{getLocationName(row.ship_to_location_id)}</span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <span className="text-sm">{row.departure_time}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{row.trip_duration_minutes}m</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {getTrailerTypeLabel(row.trailer_type_id, trailerTypes) ?? "—"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{formatDays(row.recurrence_days) || "—"}</span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <span className="text-sm">{row.start_date}</span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <span className="text-sm">
                      {isOpenEndedTemplate(row.end_date) ? "No end date" : row.end_date}
                    </span>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      {/* Multi-leg templates now round-trip through
                          OrderFormDialog `templateEditMode` via the shared
                          template<->form helpers, so no gate is needed. */}
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleEditClick(template)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDelete(template.id)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
                })}
              </Fragment>
            ))}
          </TableBody>
        </Table>
          </div>
          }
        />
        <TablePagination
          page={pagination.page}
          pageSize={pagination.pageSize}
          totalCount={pagination.totalCount}
          totalPages={pagination.totalPages}
          onPageChange={pagination.setPage}
          onPageSizeChange={pagination.setPageSize}
        />
        </>
      )}
    </div>
  );
}
